import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Button, TextField, Box, Grid, Stack } from '@mui/material';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../../../reusable-components/iconify/Iconify';
import AccordionWrapper from '../../../../../reusable-components/accordion-wrapper/AccordionWrapper';

OrderGuideVendorPreferencesBulkLoad.propTypes = {
  vendors: PropTypes.array,
  allSubcategories: PropTypes.array,
  facilities: PropTypes.array,
  categories: PropTypes.array,
  groups: PropTypes.array,
  isRestricted: PropTypes.bool,
  setFormData: PropTypes.func
};

export default function OrderGuideVendorPreferencesBulkLoad({ categories, facilities, groups, vendors, allSubcategories, isRestricted, setFormData }) {

  const { enqueueSnackbar } = useSnackbar();
  const [selectedToDelete, setSelectedToDelete] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [vendorPreferences, setVendorPreferences] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    setSubcategories(allSubcategories.map((subCat) => ({ ...subCat, mainCategory: categories.find(cat => cat.value === subCat.mainCategoryId)?.label })));
  }, [categories, allSubcategories])

  const cols = [
    {
      field: 'agoraCategory',
      headerName: 'Category',
      minWidth: 80,
      flex: 1,
      type: 'customText',
    },
    {
      field: 'agoraSubcategory',
      headerName: 'Subcategory',
      minWidth: 80,
      flex: 1,
      type: 'customText',
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      minWidth: 80,
      flex: 1,
      type: 'customText',
    }
  ]

  const defaultAutocompleteSettings = {
    multiple: true,
    fullWidth: true,
    disableCloseOnSelect: true,
    limitTags: 7,
    size: "small",
    sx: { '& span.MuiAutocomplete-tagSizeSmall ': { color: 'primary.main' } }
  };

  const defaultGirdSettings = {
    boxSX: { height: 300 },
    scrollbarHeight: 56,
    gridColumns: cols,
    onRowSelectionModelChange: (newSelection) => {
      setSelectedToDelete(newSelection);
    },
    rowSelectionModel: selectedToDelete ?? [],
    checkboxSelection: true,
    displayCheckboxRight: true,
    CustomLeftToolbar: () => <>
      <Box>{(isRestricted ? "Restricted" : "Preferred")} Vendor List</Box>
      {selectedToDelete?.length > 0 && <Button
        size="small"
        color="error"
        variant="text"
        startIcon={<Iconify icon="ph:check-fill" />}
        onClick={() => {
          setVendorPreferences(preferences => [...preferences.filter(preference => !selectedToDelete.includes(preference.id))]);
          setSelectedToDelete(null)
        }}
      >
        Delete selected items
      </Button>}
    </>
  }

  useEffect(() => {
    setFormData({
      categories: selectedCategories,
      facilities: selectedFacilities?.map(facility => facility.value),
      vendorPreferences: vendorPreferences.map(pref => ({
        subcategoryId: pref.agoraSubcategoryId,
        vendorId: pref.vendorId, 
      })),
      isRestricted
    });
  }, [selectedFacilities, selectedCategories, vendorPreferences]); 

  return <>
    <Stack sx={{ mt: 2 }} spacing={2}>
      <Autocomplete
        {...defaultAutocompleteSettings}
        value={selectedGroups}
        onChange={(event, newValue) => {
          if (newValue.some((option) => option.all)) {
            setSelectedGroups(groups);
            setSelectedFacilities(facilities);
            return;
          }
          setSelectedGroups(newValue);
          const newVals = newValue.map(v => v.value);
          setSelectedFacilities(facilities.filter(f => newVals.includes(f.extraValue)));
        }}
        options={[{ label: 'Select All...', all: true }, ...groups]}
        renderInput={(params) => <TextField {...params} label="Groups" autoComplete="off"/>}
      />
      <Autocomplete
        {...defaultAutocompleteSettings}
        value={selectedFacilities}
        onChange={(event, newValue) => setSelectedFacilities(newValue.some((option) => option.all) ? facilities : newValue)}
        options={[{ label: 'Select All...', all: true }, ...facilities]}
        renderInput={(params) => <TextField {...params} label="Facilities" autoComplete="off"/>}
      />
      <Autocomplete
        {...defaultAutocompleteSettings}
        value={selectedCategories}
        options={[{ label: 'Select All...', all: true }, ...categories]}
        onChange={(event, newValue) => {         
          newValue = newValue.some((option) => option.all) ? categories : newValue;
          setSelectedCategories(newValue);
          const categoryIds = newValue.map(val => val.value);
          setSelectedSubcategories(subcategories.filter(subcategory => categoryIds.includes(subcategory.mainCategoryId)));
        }}
        renderInput={(params) => <TextField {...params} label="Categories" autoComplete="off"/>}
      />
      <Box>
        <Autocomplete
          {...defaultAutocompleteSettings}
          options={isRestricted ? [{ label: 'Select All...', all: true }, ...vendors] : vendors}
          value={(vendorPreferences ?? []).reduce((acc, preference) => {
            acc[preference.vendorId] = { value: preference.vendorId, label: preference.vendor }
            return acc;
          }, []).filter(preference => preference !== null)}
          isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
          onChange={(e, newValue) => setVendorPreferences(preferences => {          
            if (newValue.length === 0) return [];
            if (newValue.some((option) => option.all)) {
              return vendors.flatMap(vendor => selectedSubcategories.map(subcategory => ({
                agoraSubcategoryId: subcategory.id,
                agoraCategoryId: subcategory.mainCategoryId,
                agoraSubcategory: subcategory.name,
                agoraCategory: subcategory.mainCategory,
                vendor: vendor.label,
                vendorId: vendor.value
              }))).map((preference, index) => ({ ...preference, id: index }));
            }
            const oldVendors = preferences.map(val => val.vendorId);
            const newValues = newValue.map(val => val.value);
            const newVendors = newValues.filter(val => !oldVendors.includes(val));
            const filteredPreferences = preferences.filter(preference => newValues.includes(preference.vendorId));
            if (newVendors.length === 0) return filteredPreferences;
            const vendorName = vendors.find(vendor => vendor.value === newVendors[0])?.label;
            const oldSubcats = filteredPreferences.filter(preference => !isRestricted || preference.vendorId === newVendors[0])
              .map(preference => preference.agoraSubcategoryId);
            if (oldSubcats.length === selectedSubcategories.length) enqueueSnackbar("All possible categories already filled", { variant: "error" });
            return [...filteredPreferences,
            ...selectedSubcategories.filter(subcategory => !oldSubcats.includes(subcategory.id))
              .map(subcategory => ({
                agoraSubcategoryId: subcategory.id,
                agoraCategoryId: subcategory.mainCategoryId,
                agoraSubcategory: subcategory.name,
                agoraCategory: subcategory.mainCategory,
                vendor: vendorName,
                vendorId: newVendors[0]
              }))].map((preference, index) => ({ ...preference, id: index }));
          })}
          renderInput={(params) => <TextField {...params} label="Autofill Vendor" autoComplete="off"/>}
        />
      </Box>
      <Box>
        <Stack spacing={2}>
          {selectedCategories?.map((category, index) => <AccordionWrapper key={index} title={category.label} defaultExpanded={index === 0} unmountOnExit>
              <Grid container spacing={2}>
                {selectedSubcategories.filter(subcategory => subcategory.mainCategoryId === category.value).map((subcat, index) =>
                  <Grid sx={{ mt: index < 2 ? 0 : undefined }} key={index} item xs={6}>
                    <Box>{subcat.name}</Box>
                    <Autocomplete
                      size="small"
                      fullWidth
                      isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                      renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                      sx={{
                        '& legend': { display: 'none' },
                        '& fieldset': { top: 0 },
                        '& .MuiFormLabel-root ': { display: 'none' },
                        '& span.MuiAutocomplete-tagSizeSmall ': { color: 'primary.main' },
                      }}
                      options={vendors ?? []}
                      limitTags={isRestricted ? 3 : -1}
                      multiple={isRestricted}
                      value={isRestricted ?
                        (vendorPreferences ?? []).filter((preference) => preference.agoraSubcategoryId === subcat.id)
                          .map((preference) => ({ value: preference.vendorId, label: preference.vendor })) :
                        vendors?.find(vendor => vendor.value === vendorPreferences?.find(pref => pref.agoraSubcategoryId === subcat.id)?.vendorId) ?? null}
                      onChange={(e, newValue) => {
                        setVendorPreferences(preferences => [
                        ...preferences.filter(preference => preference.agoraSubcategoryId !== subcat.id),
                        ...(newValue ? isRestricted ? newValue : [newValue] : [])
                          .map(value => ({
                            agoraSubcategoryId: subcat.id,
                            agoraCategoryId: subcat.mainCategoryId,
                            agoraSubcategory: subcat.name,
                            agoraCategory: subcat.mainCategory,
                            vendor: value.label,
                            vendorId: value.value
                          }))
                        ].map((val, ind) => ({ ...val, id: ind })));
                      }}
                    />
                  </Grid>)}
              </Grid>
            </AccordionWrapper>)}
        </Stack>
      </Box>
    </Stack>

    <Box sx={{ mt: 3 }}>
      {(vendorPreferences && vendorPreferences.length > 0) && <CustomDataGrid
        gridId="order-guide-vendor-preferences-bulk"
        data={vendorPreferences ?? []}
        {...defaultGirdSettings}
      />}
    </Box>
  </>;
}
